import type { CabinetBaseType, WardrobeModule } from "../types";

export const DEFAULT_BASE_HEIGHT_MM = 72;
export const DEFAULT_BASE_SETBACK_MM = 28;
export const MIN_BASE_HEIGHT_MM = 40;
export const MAX_BASE_HEIGHT_MM = 180;
export const MAX_BASE_SETBACK_MM = 120;
export const MAX_SIDE_FILLER_MM = 250;
export const MAX_TOP_FILLER_MM = 300;

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

export const cabinetBaseType = (module: WardrobeModule): CabinetBaseType => {
  if (module.type === "mezzanine") return "floor";
  return module.baseType === "legs" || module.baseType === "floor"
    ? module.baseType
    : "plinth";
};

export const maxCabinetBaseHeightMm = (module: WardrobeModule) =>
  clamp(
    Math.round(module.height) - 228,
    MIN_BASE_HEIGHT_MM,
    MAX_BASE_HEIGHT_MM
  );

export const cabinetBaseHeightMm = (module: WardrobeModule) => {
  const type = cabinetBaseType(module);
  if (type === "floor") return 0;
  return clamp(
    Math.round(module.baseHeight ?? DEFAULT_BASE_HEIGHT_MM),
    MIN_BASE_HEIGHT_MM,
    maxCabinetBaseHeightMm(module)
  );
};

export const cabinetBaseSetbackMm = (module: WardrobeModule) =>
  clamp(
    Math.round(module.baseSetback ?? DEFAULT_BASE_SETBACK_MM),
    0,
    MAX_BASE_SETBACK_MM
  );

export const leftFillerMm = (module: WardrobeModule) =>
  clamp(Math.round(module.fillerLeft ?? 0), 0, MAX_SIDE_FILLER_MM);

export const rightFillerMm = (module: WardrobeModule) =>
  clamp(Math.round(module.fillerRight ?? 0), 0, MAX_SIDE_FILLER_MM);

export const topFillerMm = (module: WardrobeModule) =>
  clamp(Math.round(module.fillerTop ?? 0), 0, MAX_TOP_FILLER_MM);

export const installationSpanMm = (module: WardrobeModule) =>
  module.width + leftFillerMm(module) + rightFillerMm(module);

export const installationPanelAreaM2 = (
  module: WardrobeModule,
  installedHeight = module.height
) => {
  const left = leftFillerMm(module);
  const right = rightFillerMm(module);
  const top = topFillerMm(module);
  const sideBottom =
    cabinetBaseType(module) === "legs" ? cabinetBaseHeightMm(module) : 0;
  const sideHeight = Math.max(0, installedHeight - sideBottom);
  return (
    (left + right) * sideHeight +
    top * (module.width + left + right)
  ) / 1_000_000;
};

export const installationSummary = (module: WardrobeModule) => {
  if (
    module.type === "shelving" ||
    module.type === "mezzanine" ||
    module.type === "corner45"
  ) {
    return "";
  }
  const base = cabinetBaseType(module);
  const baseLabel =
    base === "floor"
      ? "без цоколя"
      : base === "legs"
        ? `ножки ${cabinetBaseHeightMm(module)} мм`
        : `цоколь ${cabinetBaseHeightMm(module)} мм, отступ ${cabinetBaseSetbackMm(module)} мм`;
  const fillers = [
    leftFillerMm(module) ? `слева ${leftFillerMm(module)} мм` : "",
    rightFillerMm(module) ? `справа ${rightFillerMm(module)} мм` : "",
    topFillerMm(module) ? `сверху ${topFillerMm(module)} мм` : ""
  ].filter(Boolean);
  return fillers.length
    ? `${baseLabel}; доборы: ${fillers.join(", ")}`
    : baseLabel;
};
